import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useScreenSize from '../../../../../../../../hooks/useScreenSize';
import AppConfirmationDialog from '../../../../../../../../components/common/AppConfirmationDialog';
import AppTextInput from '../../../../../../../../components/common/AppTextInput';
import AppButton from '../../../../../../../../components/common/AppButton';
import { SVG } from '../../../../../../../../constants/assetLists';
import {
  DepartmentFormValues,
  OnboardingUserFormValues,
  RoleFormValues,
} from '../../../../types';
import AddUsers from './AddUsers';
import MenuSection from './MenuSection';
import { ClientOption } from './NotifyDeptTreeSelect';

type Props = {
  usersCount: number;
  searchText: string;
  onSearchChange: (text: string) => void;
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isEditDisabled: boolean;
  isDeleteDisabled: boolean;
  departments: DepartmentFormValues[];
  roles: RoleFormValues[];
  clients: ClientOption[];
  users: OnboardingUserFormValues[];
  onImportUsers: (users: Omit<OnboardingUserFormValues, 'id'>[]) => void;
};

function TableHeader({
  usersCount,
  searchText,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  isEditDisabled,
  isDeleteDisabled,
  departments,
  roles,
  clients,
  users,
  onImportUsers,
}: Props) {
  const { i18n } = useTranslation();
  const isHebrew = useMemo(() => i18n.language === 'heb', [i18n.language]);
  const screenSize = useScreenSize();
  const isLarge = screenSize === 'xl' || screenSize === '2xl';
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  return (
    <>
      <div
        className={`w-full py-4 px-4 flex flex-wrap items-center justify-between gap-3 shadow-md z-2 ${
          isLarge ? (isHebrew ? 'rounded-tl-[32px]' : 'rounded-tr-[32px]') : ''
        }`}
      >
        <div className="flex-1 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <AddUsers
              onAdd={onAdd}
              departments={departments}
              roles={roles}
              clients={clients}
              existingUsers={users}
              onImportUsers={onImportUsers}
            />

            <AppButton
              variant="primary"
              py="12px"
              disabled={isEditDisabled}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.editPencilWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={onEdit}
            >
              עריכה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={isDeleteDisabled}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.whiteCloseIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={() => setShowDeleteConfirmation(true)}
            >
              מחיקה
            </AppButton>
          </div>

          <div className="flex items-center gap-3">
            <AppTextInput
              placeholder="חיפוש משתמש"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              containerClassName="!w-[499px]"
              className="bg-transparent !rounded-[28px] pl-16"
              endIcon={
                <div className="flex items-center gap-2">
                  {searchText && (
                    <div
                      onClick={() => onSearchChange('')}
                      className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer"
                    >
                      <img
                        src={SVG.closeDarkIcon}
                        alt="Clear"
                        className="w-[12px] h-[12px] object-contain"
                      />
                    </div>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      searchText && 'bg-aqua'
                    }`}
                  >
                    <img
                      src={SVG.searchIcon}
                      alt="Search"
                      className="w-[18px] h-[18px] object-contain"
                    />
                  </div>
                </div>
              }
            />
            <div>
              <button className="flex items-center gap-2.5 text-lightDarkGray font-semibold bg-grayDisabled py-2.5 px-4 rounded-full">
                <img
                  src={SVG.reWindGrayIcon}
                  alt="info"
                  className="w-4 h-4 object-contain"
                />
                <span className="m-0 text-base">הפעלת כל המשתמשים בבת אחת</span>
              </button>
            </div>
            <MenuSection
              users={users}
              departments={departments}
              roles={roles}
              clients={clients}
            />
          </div>
        </div>

        <p className="m-0 text-black">{usersCount} משתמשים</p>
      </div>

      <AppConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          onDelete();
          setShowDeleteConfirmation(false);
        }}
        icon={
          <img
            src={SVG.deleteRedIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="שים לב"
        subTitle="האם אתה בטוח שברצונך למחוק?"
        confirmBtnText="כן"
        cancelBtnText="לא"
        bordered={false}
      />
    </>
  );
}

export default TableHeader;
